import { Injectable, Injector } from '@angular/core';
import { Database, onDisconnect, set, ref, remove, onValue, DatabaseReference } from '@angular/fire/database';
import { lastValueFrom } from 'rxjs';
import { ApiService } from '../api.service';
import { API_ENDPOINT, LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { SessionStorageService } from '../session-storage.service';
import { CommonHelper } from '../helper.service';
import { FirebaseDeviceInfoService } from './firebase-device-info.service';
import { AuthService } from '@core/services/apis/auth.service';
import { DeviceLicensesService } from '@core/services/apis/device-licenses.service';
import { Mode } from '@core/constants/enum.common';
import { LocalStorageService } from '../local-storage.service';
import { TvDefaultSettingConfigService } from '../tv-default-setting-config.service';
import { TvDisplaySettingsResetService } from '../tv-display-settings-reset.service';
import { TvLoginSessionService } from '../tv-login-session.service';
import { FirebaseVersionService } from './firebase-version.service';
import { NativeDiagnosticLogService } from '@core/services/diagnostic-log/native-diagnostic-log.service';

/** Remote command written to deviceSessions/{token}. 1 = normal presence, others trigger an action. */
export enum DeviceCommand {
  Normal = 1,
  Reload = 2,
  Logout = 3,
  RefreshDeviceDb = 4,
  HealthCheck = 5,
  ResetDisplaySettingConfig = 6,
  UpdateApp = 7,
  ReloginStaff = 8,
  ReloginCust = 9,
  UpdateLogEnableOffice = 10,
  UpdateLogEnableDevice = 11
}

/**
 * Manages the device presence node deviceSessions/{token}:
 *  - request a session token from the backend
 *  - mark presence + onDisconnect.remove so the backend can detect online/offline
 *  - remove the stale session on re-register / logout
 *  - listen for remote commands written to the node (reload-* / logout-*) so the
 *    backend/console can remotely reload or sign out a specific device.
 * Extracted from the facade so all device-session logic lives in one place.
 */
@Injectable({
  providedIn: 'root'
})
export class FirebaseDeviceSessionService {

  private devicePresenceRef: DatabaseReference | null = null; // deviceSessions/{token}
  private removeCommandListener: (() => void) | null = null;  // off() for the remote-command listener
  private isReaffirming = false;  // guard against concurrent self-heal re-registrations
  private isResettingDisplaySettings = false;
  private isUpdatingApp = false;
  private isReloggingIn = false;

  constructor(
    private readonly http: ApiService,
    private db: Database,
    private sessionStorage: SessionStorageService,
    private deviceInfo: FirebaseDeviceInfoService,
    private localStorageService: LocalStorageService,
    private firebaseDeviceInfoService: FirebaseDeviceInfoService,
    private tvDefaultSettingConfigService: TvDefaultSettingConfigService,
    private deviceLicensesService: DeviceLicensesService,
    private tvDisplaySettingsResetService: TvDisplaySettingsResetService,
    private tvLoginSessionService: TvLoginSessionService,
    private firebaseVersionService: FirebaseVersionService,
    private nativeDiagnosticLogService: NativeDiagnosticLogService,
    private injector: Injector
  ) {}

  /** True once a presence ref has been created (used by the facade as a guard). */
  get isRegistered(): boolean {
    return !!this.devicePresenceRef;
  }

  /** First connect: request a new token, set presence + onDisconnect, remove the previous (different) session. */
  async register(corpPrefix: string, officeCd: string): Promise<void> {
    const previousPresenceRef = this.devicePresenceRef; // old session (e.g. previous office) to clean up
    try {
      const newPresenceRef = await this.requestPresenceRef(corpPrefix, officeCd);
      await this.markPresence(newPresenceRef);
      this.watchRemoteCommand(newPresenceRef);
      this.devicePresenceRef = newPresenceRef;
      // Different token (e.g. office changed) → remove the stale node. Same token → nothing to remove.
      if (previousPresenceRef && previousPresenceRef.key !== newPresenceRef.key) {
        await remove(previousPresenceRef);
      }
    } catch (error) {
      console.error('Failed to register device session:', error);
      // Keep the old devicePresenceRef so the current session isn't lost.
      throw error;
    }
  }

  /** Reconnect: same token reused, just re-affirm presence + onDisconnect. Never removes anything. */
  async renew(corpPrefix: string, officeCd: string): Promise<void> {
    try {
      const newPresenceRef = await this.requestPresenceRef(corpPrefix, officeCd);
      await this.markPresence(newPresenceRef);
      this.watchRemoteCommand(newPresenceRef);
      this.devicePresenceRef = newPresenceRef;
    } catch (error) {
      console.error('Failed to renew device session:', error);
      throw error;
    }
  }

  /** Remove the current presence node (logout). Falls back to deviceToken when ref isn't set. */
  async removeSession(deviceToken?: string): Promise<void> {
    this.unwatchRemoteCommand();
    if (this.devicePresenceRef) {
      await remove(this.devicePresenceRef);
    } else if (deviceToken) {
      await remove(ref(this.db, `deviceSessions/${deviceToken}`));
    }
    this.devicePresenceRef = null;
  }

  /** Mark online + auto-remove on disconnect so backend detects offline. */
  private async markPresence(presenceRef: DatabaseReference): Promise<void> {
    await onDisconnect(presenceRef).remove();
    await set(presenceRef, DeviceCommand.Normal);
  }

  /**
   * Self-heal when the presence node vanishes unexpectedly (stale onDisconnect from a
   * previous WebView). Re-runs the full registration so both the DynamoDB DeviceRegistrations
   * record (recreated via getSessionToken) and the Firebase presence node are restored.
   * Falls back to just re-affirming the Firebase node if context/backend is unavailable.
   */
  private async reaffirmPresenceAfterUnexpectedRemoval(): Promise<void> {
    if (this.isReaffirming) return; // avoid concurrent re-registrations
    this.isReaffirming = true;
    console.warn('[DeviceSession] Presence node removed unexpectedly → re-registering (DynamoDB + firebase)');
    try {
      const corpPrefix = this.localStorageService.getItem(LOCAL_STORAGE_KEY.corpPrefix) || '';
      const officeCd = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.officeSelected)?.officeCd || '';
      if (corpPrefix && officeCd) {
        this.devicePresenceRef = await this.requestPresenceRef(corpPrefix, officeCd);
        await this.markPresence(this.devicePresenceRef);
        this.watchRemoteCommand(this.devicePresenceRef);
      } else if (this.devicePresenceRef) {
        // No context to re-register in DynamoDB → at least keep the firebase node alive.
        await this.markPresence(this.devicePresenceRef);
      }
    } catch (error) {
      console.error('[DeviceSession] Re-affirm presence failed:', error);
      if (this.devicePresenceRef) {
        try {
          await this.markPresence(this.devicePresenceRef);
        } catch (presenceError) {
          console.error('[DeviceSession] Fallback presence update failed:', presenceError);
        }
      }
    } finally {
      this.isReaffirming = false;
    }
  }

  /**
   * Watch the presence node for remote commands written by the backend/console:
   *   2 → reload the page; 3 → sign out; 4 → refresh device DB. 1 = normal presence.
   */
  private watchRemoteCommand(presenceRef: DatabaseReference): void {
    this.unwatchRemoteCommand();
    this.removeCommandListener = onValue(presenceRef, (snapshot) => this.onRemoteCommand(snapshot.val()));
  }

  private unwatchRemoteCommand(): void {
    if (this.removeCommandListener) {
      this.removeCommandListener();
      this.removeCommandListener = null;
    }
  }

  private onRemoteCommand(value: unknown): void {
    if (value === null) {
      // Node vanished unexpectedly, usually a stale onDisconnect.remove() from an old WebView
      // (same deviceToken) firing after the new one re-registered. Self-heal via re-register.
      // Real logout calls unwatchRemoteCommand() first, so this listener won't fight it.
      this.reaffirmPresenceAfterUnexpectedRemoval();
      return;
    }
    const logEnabled = this.getUpdateLogEnableDeviceCommand(value);
    if (logEnabled !== null) {
      void this.applyLogEnabled(logEnabled);
      return;
    }

    if (typeof value !== 'number') return; // non-number → ignore
    if (value === DeviceCommand.Reload) {
      // Remember the TV screen so MutiScreenComponent reopens it after the reload.
      this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.remoteReloadScreen, {
        screen: this.localStorageService.getItem(LOCAL_STORAGE_KEY.screen),
        path: window.location.pathname
      });
      // CKWEB-2749: push the new web version to DeviceLicenses right after the reload (see TvStartupService).
      this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.syncVersionAfterReload, true);
      window.location.reload();
    } else if (value === DeviceCommand.Logout) {
      // setTimeout so we don't block the Firebase listener callback.
      setTimeout(() => this.performRemoteLogout(), 100);
    } else if (value === DeviceCommand.RefreshDeviceDb) {
      this.refreshDeviceDb();
    } else if (value === DeviceCommand.ResetDisplaySettingConfig) {
      void this.resetDisplaySettingConfig();
    } else if (value === DeviceCommand.UpdateApp) {
      void this.updateApp();
    } else if (value === DeviceCommand.ReloginStaff) {
      void this.reloginStaff();
    } else if (value === DeviceCommand.ReloginCust) {
      void this.reloginCust();
    } else if (value === DeviceCommand.UpdateLogEnableOffice) {
      void this.updateLogEnableOffice();
    } else if (value === DeviceCommand.UpdateLogEnableDevice) {
      void this.updateLogEnableDevice();
    }
  }

  /** Refresh registration presence and device metadata in their respective tables. */
  private async refreshDeviceDb(): Promise<void> {
    const deviceToken = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.deviceToken);
    if (!deviceToken) return;
    try {
      const url = `${API_ENDPOINT.firebase.refreshDeviceDb}`;
      await Promise.all([
        lastValueFrom(this.http.post(url, { deviceToken })),
        this.deviceLicensesService.syncDeviceMetadata(true)
      ]);
    } catch (error) {
      console.error('Failed to refresh device DB:', error);
    } finally {
      // Reset the node back to presence (1) so the command isn't re-triggered and the device stays online.
      if (this.devicePresenceRef) {
        try {
          await set(this.devicePresenceRef, DeviceCommand.Normal);
        } catch (error) {
          console.error('Failed to reset device command to normal:', error);
        }
      }
    }
  }

  private async performRemoteLogout(): Promise<void> {
    try {
      await this.injector.get(AuthService).logout();
    } catch (error) {
      console.error('Remote logout failed:', error);
      this.sessionStorage.clearSession();
      window.location.href = `/auth`;
    }
  }

  /** Fetch a session token from the backend and build the presence ref. */
  private async requestPresenceRef(corpPrefix: string, officeCd: string): Promise<DatabaseReference> {
    const loginSession = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.loginSession);
    const refreshToken = loginSession?.refreshToken;
    if (!refreshToken) {
      throw new Error('Cannot register Firebase device session without a refresh token.');
    }
    const res = await this.requestDeviceSessionToken(corpPrefix, officeCd, refreshToken);
    const deviceToken = res?.result;
    if (typeof deviceToken !== 'string' || !deviceToken.trim()) {
      throw new Error('Backend did not return a valid Firebase device token.');
    }
    this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.deviceToken, deviceToken);
    return ref(this.db, `deviceSessions/${deviceToken}`);
  }

  private async requestDeviceSessionToken(corpPrefix: string, officeCd: string, refreshToken: string) {
    let sessionsFbId = "";
    const mode = this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode) || Mode.WEB;

    if (mode === Mode.TV) {
      let deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId() || '';
      sessionsFbId = CommonHelper.generateSessionFirebaseID(deviceId);
    } else {
      sessionsFbId = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.sessionsFBId) || '';
      if (!sessionsFbId) {
        sessionsFbId = CommonHelper.generateSessionFirebaseID();
      }
    }

    this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.sessionsFBId, sessionsFbId);

    const url = `${API_ENDPOINT.realtimedb.getSessionToken}`;
    const body = {
      'corpprefix': corpPrefix,
      'officecd': officeCd,
      'sessionsFBId': sessionsFbId,
      'refreshToken': refreshToken
    };
    return await lastValueFrom(this.http.post(url, body));
  }

  private async resetDisplaySettingConfig(): Promise<void> {
    if (this.isResettingDisplaySettings) return;

    this.isResettingDisplaySettings = true;
    try {
      const reset = await this.tvDefaultSettingConfigService.resetForCurrentOffice();
      if (!reset) return;

      await this.deviceLicensesService.syncSettingConfig();
      this.tvDisplaySettingsResetService.notify(); //notify to menu-right update immediatly new state
    } catch (error) {
      console.error('Failed to reset TV display setting config:', error);
    } finally {
      this.isResettingDisplaySettings = false;
      await this.restoreNormalState('resetting settings');
    }
  }

  private async updateApp(): Promise<void> {
    if (this.isUpdatingApp) return;

    this.isUpdatingApp = true;
    try {
      const updateRequested = await this.firebaseVersionService.requestTvAppUpdate();
      if (!updateRequested) {
        console.warn('Remote app update ignored because AndroidBridge is unavailable.');
      }
    } catch (error) {
      console.error('Failed to request TV app update:', error);
    } finally {
      this.isUpdatingApp = false;
      await this.restoreNormalState('requesting an app update');
    }
  }

  private async updateLogEnableOffice(): Promise<void> {
    try {
      const logEnabled = await this.deviceLicensesService.updateLogEnableOffice();
      if (logEnabled !== null) {
        this.nativeDiagnosticLogService.setLogEnabled(logEnabled);
      }
    } finally {
      await this.restoreNormalState('synchronizing Saucer log status');
    }
  }

  private async updateLogEnableDevice(): Promise<void> {
    try {
      const deviceId = this.firebaseDeviceInfoService.getAndroidDeviceId();
      if (deviceId) {
        const logEnabled = await this.deviceLicensesService.getLogEnabledByDeviceId(deviceId);
        this.nativeDiagnosticLogService.setLogEnabled(logEnabled);
      }
    } finally {
      await this.restoreNormalState('reloading Saucer log status');
    }
  }

  private getUpdateLogEnableDeviceCommand(value: unknown): number | null {
    if (!value || typeof value !== 'object') return null;

    const command = value as { action?: unknown; logEnabled?: unknown };
    return command.action === DeviceCommand.UpdateLogEnableDevice &&
      (command.logEnabled === 0 || command.logEnabled === 1)
      ? command.logEnabled
      : null;
  }

  private async applyLogEnabled(logEnabled: number): Promise<void> {
    try {
      if (await this.deviceLicensesService.applyLogEnabled(logEnabled)) {
        this.nativeDiagnosticLogService.setLogEnabled(logEnabled);
      }
    } finally {
      await this.restoreNormalState('applying Saucer log status');
    }
  }

  private async restoreNormalState(actionName: string): Promise<void> {
    if (!this.devicePresenceRef) return;

    try {
      await set(this.devicePresenceRef, DeviceCommand.Normal);
    } catch (error) {
      console.error(`Failed to restore device presence after ${actionName}:`, error);
    }
  }

  private async reloginStaff(): Promise<void> {
    await this.performRelogin();
  }

  private async reloginCust(): Promise<void> {
    await this.performRelogin();
  }

  private async performRelogin(): Promise<void> {
    if (this.isReloggingIn) return;

    this.isReloggingIn = true;
    try {
      const refreshToken = this.sessionStorage.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token is available for automatic re-login.');
      }

      const authService = this.injector.get(AuthService);
      const refreshResponse: any = await lastValueFrom(authService.requestAccessToken(refreshToken));
      const refreshedSession = refreshResponse?.result;
      if (!refreshedSession?.accessToken || !refreshedSession?.refreshToken) {
        throw new Error('The refresh-token response did not include a valid session.');
      }

      await authService.applyRefreshedSession(refreshedSession);
      this.tvLoginSessionService.persistCurrentSession();
      window.location.reload();
    } catch (error) {
      console.error('Automatic remote re-login failed:', error);
    } finally {
      this.isReloggingIn = false;
    }
  }
}
